import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Eye, Save, Send, Info, Sparkles, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import FileAttachments from "../components/FileAttachments";
import { Attachment } from "../types";

export default function PolicyNewPage() {
  const { currentUser } = useAuth();
  const { users, policies, refreshPolicies, approvers, fetchApprovers } = useData();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    policyNumber: "",
    description: "",
    effectiveDate: new Date().toISOString().split('T')[0], // Set to today's date
    expiryDate: "",
    reviewDate: "",
    approvedBy: "",
    requireSignature: false
  });

  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState("");
  const [draftPolicyId, setDraftPolicyId] = useState<string | null>(null);
  const [draftAttachments, setDraftAttachments] = useState<Attachment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewMode, setPreviewMode] = useState(false);

  // AI Enhancement State
  const [aiState, setAiState] = useState({
    isLoading: false,
    showPreview: false,
    currentField: '',
    currentAction: '',
    result: '',
    originalValue: ''
  });

  useEffect(() => {
    if (approvers.length === 0) {
      fetchApprovers();
    }
  }, []);

  // Create a blank draft policy in the background so we have an ID for attachments
  useEffect(() => {
    const createDraft = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
        const response = await fetch(`${API_BASE}/api/policies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            title: '(Draft in Progress)',
            description: '-',
            content: '-',
            status: 'Draft',
            requireSignature: false,
            effectiveDate: new Date().toISOString().split('T')[0],
            version: 1,
          }),
        });
        const data = await response.json();
        if (data.success && data.policy) {
          setDraftPolicyId(data.policy.id);
        }
      } catch (error) {
        console.error('Failed to create draft policy:', error);
      }
    };
    createDraft();
  }, []);

  // Keyboard shortcuts for AI enhancements
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        switch (event.key.toLowerCase()) {
          case 'p':
            event.preventDefault();
            // Determine which field to polish based on current focus or last active field
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement?.id === 'description') {
              handleAiEnhancement('description', 'polish');
            } else if (activeElement?.id === 'content') {
              handleAiEnhancement('content', 'polish');
            } else {
              // Default to content if no specific field is focused
              handleAiEnhancement('content', 'polish');
            }
            break;
          case 'g':
            event.preventDefault();
            // Determine which field to generate based on current focus
            const activeEl = document.activeElement as HTMLElement;
            if (activeEl?.id === 'description') {
              handleAiEnhancement('description', 'generate');
            } else if (activeEl?.id === 'content') {
              handleAiEnhancement('content', 'generate');
            } else {
              // Default to content if no specific field is focused
              handleAiEnhancement('content', 'generate');
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [formData.title, formData.description, content, aiState.isLoading]);



  // Filter users for policy approvers (Admin and Director only)
  const getEligibleApprovers = () => {
    return users.filter(user =>
      !user.archived
    );
  };

  // Handle effective date change and clear dependent dates if needed
  const handleEffectiveDateChange = (newEffectiveDate: string) => {
    const updatedFormData = { ...formData, effectiveDate: newEffectiveDate };

    // If effective date is cleared, clear and disable review/expiry dates
    if (!newEffectiveDate) {
      updatedFormData.reviewDate = "";
      updatedFormData.expiryDate = "";
    } else {
      // If new effective date is after current review/expiry dates, clear them
      if (formData.reviewDate && new Date(formData.reviewDate) <= new Date(newEffectiveDate)) {
        updatedFormData.reviewDate = "";
      }
      if (formData.expiryDate && new Date(formData.expiryDate) <= new Date(newEffectiveDate)) {
        updatedFormData.expiryDate = "";
      }
    }

    setFormData(updatedFormData);

    // Clear related errors if they exist
    if (errors.expiryDate || errors.reviewDate) {
      const newErrors = { ...errors };
      delete newErrors.expiryDate;
      delete newErrors.reviewDate;
      setErrors(newErrors);
    }
  };

  // AI Enhancement Functions
  const callChatGPT = async (prompt: string, content: string): Promise<string> => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/ai/enhance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt,
          content
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get AI enhancement');
      }

      const data = await response.json();
      return data.result;
    } catch (error) {
      console.error('AI Enhancement Error:', error);
      throw error; // Pass through the actual error message
    }
  };

  const handleAiEnhancement = async (field: string, action: string) => {
    let prompt = '';
    let contentToEnhance = '';
    let originalValue = '';

    // Determine prompt and content based on field and action
    switch (field) {
      case 'description':
        if (action === 'polish') {
          prompt = "Polish the following policy description. Improve clarity, grammar, and flow without changing the meaning. Make it more professional and formal. Keep the response to exactly two sentences. Do not use markdown formatting like ** or headings.";
          contentToEnhance = formData.description;
          originalValue = formData.description;
        } else if (action === 'generate') {
          prompt = `Based on the policy title: "${formData.title}", generate a comprehensive description of the policy. Summarise the purpose and scope of the policy clearly. Use a formal tone. Keep the response to exactly two sentences. Avoid markdown formatting (e.g., no ** or headings).`;
          contentToEnhance = formData.title;
          originalValue = formData.description;
        }
        break;

      case 'content':
        if (action === 'polish') {
          prompt = "Polish the following draft policy. Make it more elaborate, clear, and professional. Enhance the tone to be formal and company-appropriate. Do not change the policy's intent. Remove any markdown-style formatting like '**'. Use clear section headings without asterisks or markdown formatting.";
          contentToEnhance = content;
          originalValue = content;
        } else if (action === 'generate') {
          prompt = `Generate a formal and detailed company policy based on this description: "${formData.description}". Make the policy comprehensive and well-structured. Use clear section headings (without asterisks or markdown formatting). Elaborate on responsibilities, procedures, and compliance. Write in a formal tone suitable for internal HR or operational documentation. Do not include '**' or other markdown-style formatting.`;
          contentToEnhance = formData.description;
          originalValue = content;
        }
        break;
    }

    if (!contentToEnhance.trim()) {
      toast.error(`Please enter ${field === 'title' ? 'a title' : field === 'description' ? 'a description' : 'content'} first`);
      return;
    }

    setAiState({
      isLoading: true,
      showPreview: false,
      currentField: field,
      currentAction: action,
      result: '',
      originalValue
    });

    try {
      const result = await callChatGPT(prompt, contentToEnhance);

      // Clean up markdown formatting for content field
      const cleanedResult = field === 'content' ?
        result.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1') :
        result;

      setAiState({
        isLoading: false,
        showPreview: true,
        currentField: field,
        currentAction: action,
        result: cleanedResult,
        originalValue
      });
    } catch (error) {
      console.error('AI Enhancement Error:', error);
      setAiState({
        isLoading: false,
        showPreview: false,
        currentField: '',
        currentAction: '',
        result: '',
        originalValue: ''
      });

      const errorMessage = error instanceof Error ? error.message : 'Failed to enhance content';
      toast.error(errorMessage);
    }
  };

  const handleAiAccept = () => {
    const { currentField, result } = aiState;

    switch (currentField) {
      case 'description':
        setFormData(prevFormData => ({ ...prevFormData, description: result }));
        break;
      case 'content':
        setContent(result);
        break;
    }

    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });

    toast.success('AI enhancement applied successfully');
  };

  const handleAiCancel = () => {
    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });
  };

  const validateForm = (isDraft = false) => {
    const newErrors: Record<string, string> = {};

    // Required fields for both draft and published policies
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!content.trim()) newErrors.content = "Content is required";
    if (!formData.effectiveDate) newErrors.effectiveDate = "Effective date is required";

    // Additional validation for published policies
    if (!isDraft) {
      if (!formData.approvedBy) newErrors.approvedBy = "Approver is required for published policies";
    }

    // Date validations
    if (formData.effectiveDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.effectiveDate)) {
        newErrors.expiryDate = "Expiry date must be after effective date";
      }
    }

    if (formData.effectiveDate && formData.reviewDate) {
      if (new Date(formData.reviewDate) <= new Date(formData.effectiveDate)) {
        newErrors.reviewDate = "Review date must be after effective date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (status: 'Draft' | 'Active') => {
    const isValid = validateForm(status === 'Draft');
    if (!isValid || !currentUser) return;

    setIsSaving(true);
    try {
      if (!draftPolicyId) {
        toast.error('Draft policy not ready yet. Please try again in a moment.');
        setIsSaving(false);
        return;
      }

      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/policies/${draftPolicyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          title: formData.title,
          policyNumber: formData.policyNumber || undefined,
          description: formData.description,
          status: status,
          content: content,
          requireSignature: formData.requireSignature,
          effectiveDate: formData.effectiveDate,
          expiryDate: formData.expiryDate || undefined,
          reviewDate: formData.reviewDate || undefined,
          approvedBy: formData.approvedBy || undefined,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to save policy');
      }

      await refreshPolicies();
      toast.success(`Policy ${status === 'Draft' ? 'saved as draft' : 'published'} successfully!`);
      navigate("/policies");
    } catch (error) {
      console.error(`Failed to ${status === 'Draft' ? 'save draft' : 'publish'} policy:`, error);
      toast.error(`Failed to ${status === 'Draft' ? 'save draft' : 'publish'} policy`);
    } finally {
      setIsSaving(false);
    }
  };

  if (previewMode) {
    return (
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setPreviewMode(false)} className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold leading-tight">Preview: {formData.title || "Untitled"}</h1>
              <p className="text-sm text-muted-foreground">This is how the policy will appear to users.</p>
            </div>
          </div>
          <div className="w-full md:w-auto">
            <div className="grid grid-cols-2 md:flex md:items-center gap-2">
              <Button variant="outline" className="w-full" onClick={() => setPreviewMode(false)}>
                Back to Edit
              </Button>
              <Button variant="outline" className="w-full" onClick={() => handleSave('Draft')} disabled={isSaving}>
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Saving..." : "Save Draft"}
              </Button>
              <Button className="w-full col-span-2 md:col-auto" onClick={() => handleSave('Active')} disabled={isSaving}>
                <Send className="h-4 w-4 mr-2" />
                {isSaving ? "Publishing..." : "Publish"}
              </Button>
            </div>
          </div>
        </div>

        {/* Policy Content Card */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl md:text-2xl">{formData.title || "Untitled Policy"}</CardTitle>
              <p className="text-sm text-muted-foreground">
                Policy • Version 1 • Effective: {formData.effectiveDate ? new Date(formData.effectiveDate).toLocaleDateString() : "Not set"}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Description Section */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground border-b pb-2">Description</h3>
              <p className="text-muted-foreground">
                {formData.description || "No description provided."}
              </p>
            </div>

            {/* Content Section */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground border-b pb-2">Content</h3>
              {/* The 'prose' class helps with typography and readability */}
              <div className="prose prose-sm md:prose-base max-w-none text-foreground">
                <div dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') || "<p>No content provided.</p>" }} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/policies">
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">New Policy</h1>
            <p className="text-sm text-muted-foreground">Create a new policy document</p>
          </div>
        </div>

        <div className="w-full md:w-auto">
          <div className="grid grid-cols-2 md:flex md:items-center gap-2">
            <Button variant="outline" className="w-full" onClick={() => setPreviewMode(true)}>
              <Eye className="h-4 w-4 mr-2" /> Preview
            </Button>
            <Button variant="outline" className="w-full" onClick={() => handleSave('Draft')} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" /> {isSaving ? "Saving..." : "Save Draft"}
            </Button>
            <Button className="w-full col-span-2 md:col-auto" onClick={() => handleSave('Active')} disabled={isSaving}>
              <Send className="h-4 w-4 mr-2" /> {isSaving ? "Publishing..." : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form Column */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Policy Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Enter policy title" className={errors.title ? "border-red-500" : ""} maxLength={64} />
                <p className={`text-sm text-right ${formData.title.length >= 60 ? 'text-red-500' : formData.title.length >= 50 ? 'text-amber-500' : 'text-muted-foreground'}`}>{formData.title.length}/64</p>
                {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
              </div>

              {/* Policy Number */}
              <div className="space-y-2">
                <Label htmlFor="policyNumber">Policy Number</Label>
                <Input id="policyNumber" value={formData.policyNumber} onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })} placeholder="Optional unique identifier" className={errors.policyNumber ? "border-red-500" : ""} />
                {errors.policyNumber && <p className="text-sm text-red-500">{errors.policyNumber}</p>}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" rows={10} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of the policy" className={errors.description ? "border-red-500" : ""} />
                {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="effectiveDate">Effective Date *</Label>
                  <Input id="effectiveDate" type="date" value={formData.effectiveDate} onChange={(e) => handleEffectiveDateChange(e.target.value)} className={errors.effectiveDate ? "border-red-500" : ""} />
                  {errors.effectiveDate && <p className="text-sm text-red-500">{errors.effectiveDate}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reviewDate">Review Date</Label>
                  <Input id="reviewDate" type="date" value={formData.reviewDate} onChange={(e) => setFormData({ ...formData, reviewDate: e.target.value })} min={formData.effectiveDate || undefined} disabled={!formData.effectiveDate} className={errors.reviewDate ? "border-red-500" : ""} />
                  {errors.reviewDate && <p className="text-sm text-red-500">{errors.reviewDate}</p>}
                </div>
              </div>

              {/* Approver and Signature */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="approvedBy">Approved By</Label>
                  <Select value={formData.approvedBy} onValueChange={(value) => setFormData({ ...formData, approvedBy: value })}>
                    <SelectTrigger className={errors.approvedBy ? "border-red-500" : ""}><SelectValue placeholder="Select approver" /></SelectTrigger>
                    <SelectContent>{approvers.map((user) => (<SelectItem key={user.id} value={user.id}>{user.name} - {user.userRole}</SelectItem>))}</SelectContent>
                  </Select>
                  {errors.approvedBy && <p className="text-sm text-red-500">{errors.approvedBy}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requireSignature">Require Signature *</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch id="requireSignature" checked={formData.requireSignature} onCheckedChange={(checked) => setFormData({ ...formData, requireSignature: checked })} />
                    <span>{formData.requireSignature ? "Yes" : "No"}</span>
                  </div>
                  {errors.requireSignature && <p className="text-sm text-red-500">{errors.requireSignature}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Section */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Content / Instructions *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Enter the detailed policy content..." className={`min-h-[300px] ${errors.content ? "border-red-500" : ""}`} />
              {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
            </CardContent>
          </Card>

          {draftPolicyId && (
            <FileAttachments
              resourceType="policies"
              resourceId={draftPolicyId}
              attachments={draftAttachments}
              onAttachmentsChange={setDraftAttachments}
            />
          )}
        </div>

        {/* Sidebar Column */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Quick Tips</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3 text-muted-foreground">
              <p><strong>Save as Draft:</strong> Save your work without making it active.</p>
              <p><strong>Save & Publish:</strong> Make the policy active and available.</p>
              <p><strong>AI Enhancement:</strong> Use Alt+P to polish or Alt+G to generate content.</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Version Information</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p>This will be version <strong>1</strong> of this policy.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Enhancement Preview Modal */}
      <Dialog open={aiState.showPreview} onOpenChange={handleAiCancel}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Enhancement Preview
            </DialogTitle>
            <DialogDescription>
              Review the AI-generated {aiState.currentAction === 'polish' ? 'polished' : 'generated'} {aiState.currentField} and choose to accept or cancel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Original:</Label>
              <div className="mt-1 p-3 bg-muted/50 rounded-md border">
                {aiState.currentField === 'content' ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: aiState.originalValue || '<p>No content</p>' }} />
                ) : (
                  <p className="text-sm">{aiState.originalValue || `No ${aiState.currentField}`}</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-green-600">AI Enhanced:</Label>
              <div className="mt-1 p-3 bg-green-50 rounded-md border border-green-200">
                {aiState.currentField === 'content' ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: aiState.result }} />
                ) : (
                  <p className="text-sm">{aiState.result}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleAiCancel}>
              Cancel
            </Button>
            <Button onClick={handleAiAccept} className="bg-green-600 hover:bg-green-700">
              Accept Enhancement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}